import { RefObject, useEffect, useRef, useState } from 'react'
import Janus from '../vendor/janus'

type StreamStatus = 'idle' | 'connecting' | 'playing' | 'error'

type UseJanusStreamResult = {
    videoRef: RefObject<HTMLVideoElement>
    status: StreamStatus
    error: string | null
}

const JANUS_WS_URL = 'wss://stream.eyeridefms.com:8989/janus'
const ICE_SERVERS: RTCIceServer[] = [{ urls: 'stun:stun.l.google.com:19302' }]

const toMountpointId = (sourceId: string): number => {
    return Number(sourceId.replace(':', '0'))
}

export const useJanusStream = (sourceId: string | null, enabled: boolean): UseJanusStreamResult => {
    const videoRef = useRef<HTMLVideoElement>(null)
    const janusRef = useRef<any>(null)
    const pluginRef = useRef<any>(null)
    const remoteStreamRef = useRef<MediaStream | null>(null)

    const [status, setStatus] = useState<StreamStatus>('idle')
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        const cleanup = (): void => {
            try {
                if (pluginRef.current) {
                    pluginRef.current.send({ message: { request: 'stop' } })
                    pluginRef.current.detach()
                }
            } catch (cleanupError) {
                console.warn('Failed to detach Janus plugin cleanly', cleanupError)
            }

            try {
                if (janusRef.current) {
                    janusRef.current.destroy()
                }
            } catch (cleanupError) {
                console.warn('Failed to destroy Janus session cleanly', cleanupError)
            }

            pluginRef.current = null
            janusRef.current = null
            remoteStreamRef.current = null

            if (videoRef.current) {
                videoRef.current.srcObject = null
            }
        }

        cleanup()

        if (!enabled || !sourceId) {
            setStatus('idle')
            setError(null)
            return cleanup
        }

        const mountpointId = toMountpointId(sourceId)

        if (!Number.isFinite(mountpointId)) {
            setStatus('error')
            setError(`Invalid mountpoint id derived from source ${sourceId}`)
            return cleanup
        }

        setStatus('connecting')
        setError(null)

        let stopped = false

        Janus.init({
            debug: false,
            callback: () => {
                if (stopped) return

                if (!Janus.isWebrtcSupported()) {
                    setStatus('error')
                    setError('WebRTC is not supported in this browser')
                    return
                }

                const serverUrl = `${JANUS_WS_URL}?nocache=${Date.now()}`

                janusRef.current = new Janus({
                    server: serverUrl,
                    iceServers: ICE_SERVERS,
                    success: () => {
                        if (stopped || !janusRef.current) return

                        janusRef.current.attach({
                            plugin: 'janus.plugin.streaming',
                            success: (pluginHandle: any) => {
                                if (stopped) return

                                pluginRef.current = pluginHandle
                                pluginHandle.send({
                                    message: {
                                        request: 'watch',
                                        id: mountpointId,
                                    },
                                })
                            },
                            error: (attachError: unknown) => {
                                if (stopped) return
                                setStatus('error')
                                setError(`Failed to attach Janus streaming plugin: ${String(attachError)}`)
                            },
                            onmessage: (message: any, jsep: any) => {
                                if (stopped || !pluginRef.current) return

                                if (message?.error) {
                                    setStatus('error')
                                    setError(message.error)
                                    return
                                }

                                if (jsep) {
                                    pluginRef.current.createAnswer({
                                        jsep,
                                        tracks: [{ type: 'data' }],
                                        success: (answerJsep: any) => {
                                            if (stopped || !pluginRef.current) return

                                            pluginRef.current.send({
                                                message: { request: 'start' },
                                                jsep: answerJsep,
                                            })
                                        },
                                        error: (answerError: unknown) => {
                                            if (stopped) return
                                            setStatus('error')
                                            setError(`Failed to create Janus answer: ${String(answerError)}`)
                                        },
                                    })
                                }
                            },
                            onremotetrack: (track: MediaStreamTrack, _mid: string, added: boolean) => {
                                if (stopped) return

                                if (!remoteStreamRef.current) {
                                    remoteStreamRef.current = new MediaStream()
                                }

                                if (added) {
                                    remoteStreamRef.current.addTrack(track)
                                    if (videoRef.current) {
                                        videoRef.current.srcObject = remoteStreamRef.current
                                    }
                                    setStatus('playing')
                                } else {
                                    remoteStreamRef.current.removeTrack(track)
                                }
                            },
                            oncleanup: () => {
                                if (!stopped) {
                                    setStatus('idle')
                                }
                            },
                            detached: () => {
                                if (!stopped) {
                                    setStatus('idle')
                                }
                            },
                        })
                    },
                    error: (sessionError: unknown) => {
                        if (stopped) return
                        setStatus('error')
                        setError(`Failed to create Janus session: ${String(sessionError)}`)
                    },
                    destroyed: () => {
                        if (!stopped) {
                            setStatus('idle')
                        }
                    },
                })
            },
        })

        return () => {
            stopped = true
            cleanup()
        }
    }, [sourceId, enabled])

    return { videoRef, status, error }
}
